import type { ReactNode } from 'react';

import classNames from 'classnames';
import { useIntl } from 'react-intl';

import { DAYS_OF_WEEK } from '@src/constants/commons';
import { formatDaysOfWeek, normalizeDaysOfWeek } from '@src/packages/utils/dates/formatDaysOfWeek';

import styles from './styles.module.less';

const DAY_I18N_KEYS: Record<number, { letter: string; name: string }> = {
  1: { letter: 'general.letter.day.mon', name: 'general.day.monday' },
  2: { letter: 'general.letter.day.tue', name: 'general.day.tuesday' },
  3: { letter: 'general.letter.day.wed', name: 'general.day.wednesday' },
  4: { letter: 'general.letter.day.thu', name: 'general.day.thursday' },
  5: { letter: 'general.letter.day.fri', name: 'general.day.friday' },
  6: { letter: 'general.letter.day.sat', name: 'general.day.saturday' },
  7: { letter: 'general.letter.day.sun', name: 'general.day.sunday' },
};

export interface DaysOfWeekLabelsProps {
  days?: unknown;
  emptyPlaceholder?: ReactNode;
  className?: string;
}

const DaysOfWeekLabels = ({
  days,
  emptyPlaceholder = '-',
  className,
}: DaysOfWeekLabelsProps) => {
  const intl = useIntl();
  const normalizedDays = normalizeDaysOfWeek(days);
  const activeDays = new Set(normalizedDays);

  if (!activeDays.size) {
    return <>{emptyPlaceholder}</>;
  }

  return (
    <div
      className={classNames(styles.week, className)}
      role="group"
      aria-label={formatDaysOfWeek(normalizedDays)}
    >
      {DAYS_OF_WEEK.map(day => {
        const isActive = activeDays.has(day.value);
        const i18nKeys = DAY_I18N_KEYS[day.value];
        const dayName = intl.formatMessage({ id: i18nKeys.name });
        const dayLetter = intl.formatMessage({ id: i18nKeys.letter });

        return (
          <span
            key={day.value}
            className={classNames(styles.day, isActive && styles.dayActive)}
            title={dayName}
            aria-label={dayName}
            aria-current={isActive ? 'true' : undefined}
            data-active={isActive ? 'true' : 'false'}
          >
            {dayLetter}
          </span>
        );
      })}
    </div>
  );
};

export default DaysOfWeekLabels;
